import { qs } from 'utils';

import { MAX_SEARCH_TEXT_LENGTH } from 'src/App/constants/filter';
import type { QueryParams, QueryParamTab } from 'src/hooks/useQueryParam';

const VALID_TYPES = ['open', 'expired', 'draft'];

export const defaultQueryParams: QueryParams = {
  page: 1,
  tab: 'open',
  enabledFeatures: [],
};

interface ParseResult<T> {
  wasValid: boolean;
  parsedValue: T;
}

type NullableStringArray = Array<string | null>;

const parseTab = (
  value: string | NullableStringArray | null,
): ParseResult<QueryParamTab> => {
  const type = value?.toString() ?? defaultQueryParams.tab;

  const wasValid = VALID_TYPES.includes(type);

  return {
    wasValid,
    parsedValue: wasValid ? (type as QueryParamTab) : defaultQueryParams.tab,
  };
};

const parsePage = (
  value: string | NullableStringArray | null,
): ParseResult<number> => {
  const page = value?.toString() ?? `${defaultQueryParams.page}`;
  const pageValue = parseInt(page, 10);

  const wasValid = !Number.isNaN(pageValue) && pageValue > 0;

  return {
    wasValid,
    parsedValue: wasValid ? pageValue : defaultQueryParams.page,
  };
};

export const parseSearchText = (
  value: string | NullableStringArray | null,
): ParseResult<string | undefined> => {
  const searchText = value?.toString();

  const wasValid =
    searchText === undefined ||
    (searchText.length > 0 && searchText.length <= MAX_SEARCH_TEXT_LENGTH);

  return {
    wasValid,
    parsedValue: wasValid ? searchText : undefined,
  };
};

export const parseQueryParams = (query: string): QueryParams => {
  const originalParams = qs.parse(query, { arrayFormat: 'comma' });
  const features = originalParams.features ?? [];

  const tab = parseTab(originalParams.type);
  const page = parsePage(originalParams.page);
  const searchText = parseSearchText(originalParams.q);

  const enabledFeatures = Array.isArray(features)
    ? features
        .filter((feature) => feature !== null)
        .map((feature) => feature as string)
    : [features];

  const validParams: QueryParams = {
    tab: tab.parsedValue,
    page: page.parsedValue,
    enabledFeatures,
    q: searchText.parsedValue,
  };

  if (!(tab.wasValid && page.wasValid && searchText.wasValid)) {
    updateBrowserUrl(originalParams, validParams, true);
  }

  return validParams;
};

export const updateBrowserUrl = (
  originalParams: qs.ParsedQuery<string>,
  { tab, page, q, enabledFeatures: features }: QueryParams,
  replaceState: boolean,
): void => {
  if (window && window.history && window.location) {
    const { pathname } = window.location;
    const correctedQueryParams = {
      ...originalParams,
      type: tab === defaultQueryParams.tab ? undefined : tab,
      page: page === defaultQueryParams.page ? undefined : page,
      q: q === '' ? undefined : q,
      features,
    };
    const newUrl = qs.stringifyUrl(
      { url: `${pathname}`, query: correctedQueryParams },
      { arrayFormat: 'comma' },
    );

    if (replaceState) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }
};
